import type { FC } from 'react';
import { useEffect } from 'react';
import type { TextVariant } from '@cencosud-cencommerce/paris-uikit';
import { Button, Text } from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import type { Errors as ErrorsType } from '@/modules/product/domain/Errors';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import useErrorsType from '@/sections/shippingMethods/hooks/useErrorsType';

export interface ErrorsProps extends ErrorsType {
  onClick?: () => void;
  className?: string;
  titleVariant?: TextVariant;
  messageVariant?: TextVariant;
  iconSize?: string;
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  customIcon?: JSX.Element;
  'data-testid'?: string;
}

const ErrorPage: FC<ErrorsProps> = ({
  variant,
  title,
  message,
  buttonLabel,
  onClick,
  customIcon,
  className,
  iconSize,
  titleVariant,
  messageVariant,
  buttonSize,
  'data-testid': dataTestId = 'paris-cart-errors'
}) => {
  const {
    title: defaultTitle,
    message: defaultMessage,
    icon: defaultIcon,
    buttonLabel: defaultButtonLabel
  } = useErrorsType(variant);

  const iconWidth = iconSize ? `${iconSize}px` : '220px';
  const iconHeight = iconSize ? `${iconSize}px` : '220px';

  useEffect(() => {
    if (variant === 'something-went-wrong') {
      SendShippingEvents.sendEvent({
        event: 'baseEvent',
        extraFields: {
          'event-interactive-accion':
            ShippingEventInteractiveActions.ShippingErrors,
          'event-interactive-category':
            ShippingEventInteractiveCategories.Checkout,
          'event-interactive-tag': ShippingEventInteractiveTags.ShippingError
        }
      });
    }
  }, [variant]);

  const sendTryAgainShipping = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.ShippingErrors,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag':
          ShippingEventInteractiveTags.ShippingErrorTryAgain
      }
    });
  };

  return (
    <div
      className={classNames(
        'co-flex co-flex-col co-items-center co-justify-center co-gap-5 co-py-[88px]',
        className
      )}
      data-testid={dataTestId}
    >
      <div
        className={classNames('co-flex co-justify-center co-items-center')}
        style={{
          width: iconWidth,
          height: iconHeight
        }}
      >
        {customIcon || defaultIcon}
      </div>

      <div className="co-flex co-flex-col co-justify-center co-items-center co-gap-4 co-text-center co-py-8 co-px-6">
        <Text
          variant={titleVariant ?? 'subtitle1'}
          weight={700}
          color="primary-500"
          className="co-max-w-[700px]"
        >
          {title || defaultTitle}
        </Text>
        <Text
          variant={messageVariant ?? 'body1'}
          className="co-max-w-[600px]"
          weight={400}
          color="neutral-900"
        >
          {message || defaultMessage}
        </Text>
      </div>

      {onClick && (
        <Button
          onClick={() => {
            onClick();
            sendTryAgainShipping();
          }}
          variant="primary"
          size={buttonSize ?? 'xl'}
          className="co-w-full tablet:co-w-auto"
        >
          {buttonLabel || defaultButtonLabel}
        </Button>
      )}
    </div>
  );
};

export default ErrorPage;
